import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Pagination,
  useMediaQuery,
  useTheme,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  FileDownload,
  Search,
  VerifiedUser,
} from '@mui/icons-material';
import { Layout } from '../components/Layout/Layout';
import apiClient from '../api';
import { useAuth } from '../hooks/useAuth';

interface IAppNameResponse {
  appNames: string[];
}

interface IUserRecord {
  _id: string;
  appName: string;
  receiptId: string;
  amazonUserId: string;
  subscriptionStatus: string;
  idfa: string;
  adid?: string;
  term?: string;
  createdAt?: string;
  purchaseDate?: number;
  price?: number;
  updatedAt?: string;
}

export const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const today = new Date().toISOString().split('T')[0];
  const oneMonthAgoDate = new Date();
  oneMonthAgoDate.setMonth(oneMonthAgoDate.getMonth() - 1);
  const oneMonthAgo = oneMonthAgoDate.toISOString().split('T')[0];

  const [apps, setApps] = React.useState<string[]>([]);
  const [selectedApp, setSelectedApp] = React.useState('');
  const [startDate, setStartDate] = React.useState(oneMonthAgo);
  const [endDate, setEndDate] = React.useState(today);
  const [purchases, setPurchases] = React.useState<IUserRecord[]>([]);
  const { role } = useAuth();

  const [page, setPage] = React.useState(1);
  const limit = 20;
  const [totalRecords, setTotalRecords] = React.useState(0);

  const [validationAppName, setValidationAppName] = React.useState('');
  const [validationReceiptId, setValidationReceiptId] = React.useState('');
  const [validationAmazonUserId, setValidationAmazonUserId] = React.useState('');
  const [validationResult, setValidationResult] = React.useState<any>(null);

  const [subscriptionStatus, setSubscriptionStatus] = React.useState<string>('');
  const subscriptionStatuses = ['trial', 'converted', 'cancelled'];

  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const [selected, setSelected] = React.useState<string[]>([]);

  React.useEffect(() => {
    fetchAppsList();
  }, []);

  const fetchAppsList = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get<{ result: IAppNameResponse }>('/analytics/apps');
      setApps(data.result.appNames);
      setLoading(false);
    } catch (err) {
      setError('Failed to load apps list.');
      setLoading(false);
    }
  };

  const handleGetData = async () => {
    if (!selectedApp) {
      alert('Please select an application.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setPurchases([]);
      setPage(1);

      const queryParams = new URLSearchParams({
        appName: selectedApp,
      });
      if (startDate) {
        queryParams.append('startDate', String(Math.floor(new Date(startDate).getTime() / 1000)));
      }
      if (endDate) {
        queryParams.append('endDate', String(Math.floor(new Date(endDate).getTime() / 1000)));
      }
      if (subscriptionStatus && subscriptionStatus !== 'Any') {
        queryParams.append('subscriptionStatus', subscriptionStatus);
      }

      const { data } = await apiClient.get(`/analytics/reports?${queryParams}`);
      setLoading(false);

      if (!data.result || !Array.isArray(data.result.users)) {
        setError('No data found.');
        setPurchases([]);
        setTotalRecords(0);
        return;
      }

      setPurchases(data.result.users);
      setTotalRecords(data.result.users.length);
      setSelected([]);
    } catch (err) {
      console.error(err);
      setLoading(false);
      setError('Failed to fetch data.');
    }
  };

  const currentPageData = React.useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return purchases.slice(startIndex, endIndex);
  }, [purchases, page, limit]);

  const handleSelectAllClick = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newSelecteds = purchases.map((n) => n._id);
        setSelected(newSelecteds);
        return;
      }
      setSelected([]);
    },
    [purchases]
  );

  const isSelected = React.useCallback(
    (id: string) => selected.indexOf(id) !== -1,
    [selected]
  );

  const handleClick = React.useCallback(
    (id: string) => {
      const selectedIndex = selected.indexOf(id);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }
      setSelected(newSelected);
    },
    [selected]
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleExportCSV = () => {
    if (selected.length === 0) {
      alert('Please select at least one item to export.');
      return;
    }

    const selectedRecords = purchases.filter((record) => selected.includes(record._id));

    if (selectedRecords.length === 0) {
      alert('No valid selected items found.');
      return;
    }

    const headers = [
      'App Name',
      'Updated At',
      'Purchase Date',
      'Receipt ID',
      'Amazon User ID',
      'Subscription Status',
    ];

    const rows = selectedRecords.map((record) => [
      record.appName,
      record.updatedAt ? new Date(record.updatedAt).toLocaleString() : '',
      record.purchaseDate ? new Date(record.purchaseDate).toLocaleString() : '',
      record.receiptId,
      record.amazonUserId,
      record.subscriptionStatus === 'active' ? 'converted' : record.subscriptionStatus,
    ]);

    const csvContent =
      [headers, ...rows]
        .map((row) =>
          row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'selected_users.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportAmazonIDFA = () => {
    if (selected.length === 0) {
      alert('Please select at least one item to export.');
      return;
    }

    const selectedRecords = purchases.filter((record) => selected.includes(record._id));

    if (selectedRecords.length === 0) {
      alert('No valid selected items found.');
      return;
    }
    const header = ['Mobile Advertiser ID'];
    const rows = selectedRecords.map((record) => [record.idfa || '']);

    const csvContent =
      [header, ...rows]
        .map((row) =>
          row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'amazon_idfa.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCheckValidation = async () => {
    if (!validationAppName || !validationReceiptId || !validationAmazonUserId) {
      alert('Please fill all fields for validation check.');
      return;
    }
    try {
      const payload = {
        appName: validationAppName,
        receiptId: validationReceiptId,
        amazonUserId: validationAmazonUserId,
      };
      const { data } = await apiClient.post('/analytics/receipts/validate', payload);
      setValidationResult(data.result?.validation || data.result);
    } catch (err: any) {
      console.error(err);
      setValidationResult('Failed to validate receipt.');
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'info' | 'default' => {
    const lowerStatus = status?.toLowerCase();
    if (lowerStatus === 'active' || lowerStatus === 'converted') {
      return 'success';
    }
    if (lowerStatus === 'cancelled' || lowerStatus === 'expired') {
      return 'error';
    }
    if (lowerStatus === 'trial') {
      return 'info';
    }
    return 'default';
  };


  const PurchasesTable = React.memo<{
    currentPageData: IUserRecord[];
    isSelected: (id: string) => boolean;
    handleClick: (id: string) => void;
    handleSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    selected: string[];
    totalLength: number;
  }>(
    ({
      currentPageData,
      isSelected,
      handleClick,
      handleSelectAllClick,
      selected,
      totalLength,
    }) => {
      return (
        <TableContainer
          component={Paper}
          sx={{
            width: '100%',
            margin: '0 auto',
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 && selected.length < totalLength
                    }
                    checked={totalLength > 0 && selected.length === totalLength}
                    onChange={handleSelectAllClick}
                    inputProps={{
                      'aria-label': 'select all purchases',
                    }}
                  />
                </TableCell>
                <TableCell>App Name</TableCell>
                <TableCell>Purchase Date</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>Receipt ID</TableCell>
                <TableCell>Amazon User ID</TableCell>
                <TableCell>ADID</TableCell>
                <TableCell
                  sx={{ color: 'red', fontWeight: 'bold' }}
                >
                  Amazon IDFA
                </TableCell>
                <TableCell>Term</TableCell>
                <TableCell>Subscription Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentPageData.map((record) => {
                const isItemSelected = isSelected(record._id);
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={record._id}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onChange={() => handleClick(record._id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isSmDown ? 100 : 200,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {record.appName}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isSmDown ? 120 : 200,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {record.purchaseDate
                          ? new Date(record.purchaseDate).toLocaleString()
                          : ''}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isSmDown ? 120 : 200,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {record.updatedAt
                          ? new Date(record.updatedAt).toLocaleString()
                          : ''}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isSmDown ? 100 : 200,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {record.receiptId}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isSmDown ? 120 : 200,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {record.amazonUserId}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isSmDown ? 80 : 150,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {record.adid || ''}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isSmDown ? 120 : 200,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {record.idfa || ''}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isSmDown ? 80 : 150,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {record.term || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.subscriptionStatus === 'active' ? 'converted' : record.subscriptionStatus}
                        color={getStatusColor(record.subscriptionStatus)}
                        size="small"
                        variant="filled"
                        sx={{ fontWeight: 500, minWidth: 80 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
  );

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DashboardIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#0f172a' }}>
                Dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                View and manage subscription data across all applications
              </Typography>
            </Box>
          </Box>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Search sx={{ color: '#6366f1', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600}>
                Search Purchases
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Application</InputLabel>
                <Select
                  label="Application"
                  value={selectedApp}
                  onChange={(e: SelectChangeEvent) => {
                    setValidationAppName(e.target.value);
                    setSelectedApp(e.target.value);
                  }}
                >
                  <MenuItem value="">
                    <em>-- Select App --</em>
                  </MenuItem>
                  {apps.map((app) => (
                    <MenuItem key={app} value={app}>
                      {app}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: endDate }}
              />
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: today }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Any</InputLabel>
                <Select
                  label="Status"
                  value={subscriptionStatus}
                  onChange={(e: SelectChangeEvent) => setSubscriptionStatus(e.target.value)}
                >
                  <MenuItem value="Any">Any</MenuItem>
                  {subscriptionStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={handleGetData}>
                Get Data
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={handleExportCSV}
            disabled={selected.length === 0}
          >
            Export Selected ({selected.length})
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<FileDownload />}
            onClick={handleExportAmazonIDFA}
            disabled={selected.length === 0}
          >
            Export to Amazon
          </Button>
          {selected.length > 0 && (
            <Chip
              label={`${selected.length} selected`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography>Loading data...</Typography>
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Render memoized table */}
        <PurchasesTable
          currentPageData={currentPageData}
          isSelected={isSelected}
          handleClick={handleClick}
          handleSelectAllClick={handleSelectAllClick}
          selected={selected}
          totalLength={purchases.length}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 2 }}
        >
          <Typography variant="body2">
            Page {page}, Total Records: {totalRecords}
          </Typography>
          <Pagination
            count={Math.ceil(totalRecords / limit)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Stack>

        <Divider sx={{ my: 4 }} />
        {role === 'super-admin' && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <VerifiedUser sx={{ color: '#6366f1', fontSize: 20 }} />
                <Typography variant="h6" fontWeight={600}>
                  Validate Receipt
                </Typography>
              </Box>
              <Stack spacing={2} sx={{ maxWidth: 400 }}>
              <TextField
                size="small"
                label="Amazon User ID"
                value={validationAmazonUserId}
                onChange={(e) => setValidationAmazonUserId(e.target.value)}
              />
              <TextField
                size="small"
                label="Receipt ID"
                value={validationReceiptId}
                onChange={(e) => setValidationReceiptId(e.target.value)}
              />
              <FormControl size="small">
                <InputLabel>App Name</InputLabel>
                <Select
                  label="App Name"
                  value={validationAppName}
                  onChange={(e: SelectChangeEvent) =>
                    setValidationAppName(e.target.value)
                  }
                >
                  <MenuItem value="">
                    <em>-- Select App --</em>
                  </MenuItem>
                  {apps.map((app) => (
                    <MenuItem key={app} value={app}>
                      {app}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={handleCheckValidation}>
                Check Validation
              </Button>
            </Stack>
            </CardContent>
          </Card>
        )}
        {validationResult && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2, backgroundColor: 'grey.50' }}>
            <Typography variant="subtitle1" gutterBottom>
              Validation Result
            </Typography>
            <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {JSON.stringify(validationResult, null, 2)}
            </pre>
          </Paper>
        )}
      </Box>
    </Layout>
  );
};